import { FormOutlined, UserOutlined } from "@ant-design/icons";
import { Checkbox, DatePicker, Input, Button, Form } from "antd";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { connect, useSelector } from "react-redux";
import { searchGroupIdentifier } from "../../actions/api";
import styles from "./Group.module.css";
import dayjs from "dayjs";
import { FormValidationStates } from "../../config";
import {
  PrivacyRadioGroup,
  privacyTypes,
  resourceCategories,
  ResourceCategoryRadioGroup,
  SelfSubscriptionRadioGroup,
  selfSubscriptionTypes,
} from "../../constants";
import { DynamicGroupTypes, GroupSyncTypes } from "../../constants/enums";
import TooltipIcon from "../common/TooltipIcon";
import AssignAdministratorGroup from "./formHelpers/AssignAdministratorGroup";
import { showErrorNotification, formatDateGroup } from "../../util";
import TopicDropdown from "../common/TopicDropdown";

const helpText = (error, fieldName) => {
  return error ? (
    <label className={styles.errorText} id={`${fieldName}-error`}>
      {error}
    </label>
  ) : (
    <></>
  );
};

const warningText = (warning, fieldName) => {
  return warning ? (
    <label className={styles.warningText} id={`${fieldName}-warning`}>
      {warning}
    </label>
  ) : (
    <></>
  );
};

const formItemLayout = {
  style: { marginBottom: "0px" },
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 18,
  },
};

const MAX_GROUP_IDENTIFIER_LENGTH = 47;
const MAX_GROUP_IDENTIFIER_HARD_LIMIT = 128;

const GroupCreationForm = ({
  groupScopes,
  dynamic,
  handleSubmit,
  loading,
  searchGroupIdentifier,
}) => {
  const [form] = Form.useForm();

  const [displayNameStatus, setDisplayNameStatus] = useState("");
  const [displayNameError, setDisplayNameError] = useState("");
  const [displayNameWarning, setDisplayNameWarning] = useState("");
  const [groupIdentifierStatus, setGroupIdentifierStatus] = useState("");
  const [groupIdentifierError, setGroupIdentifierError] = useState("");
  const [groupIdentifierWarning, setGroupIdentifierWarning] = useState("");

  const identity = useSelector((state) => state.identity);

  const openNotificationsForAllValidationErrors = (errorFields = []) => {
    errorFields.forEach((field) => {
      const contents = (
        <>
          {field.errors.map((errorMessage, i) => (
            <p key={i}>{errorMessage}</p>
          ))}
        </>
      );
      const message = `Validation error for the field: "${field.name.join(
        "."
      )}"`;
      showErrorNotification(message, contents);
    });
  };

  const handleSubmitWrapper = async (values) => {
    let processedValues = { ...values, dynamic };

    const scopesEnabledByDefault = groupScopes
      .filter(
        (scope) =>
          scope.displayName === "ActiveDirectory" ||
          scope.displayName === "EmailDistributionList"
      )
      .map((scope) => scope.id);

    processedValues = {
      ...processedValues,
      syncType: processedValues.syncEgroups
        ? GroupSyncTypes.Master
        : GroupSyncTypes.NoSync,
      assignedScopes: scopesEnabledByDefault,
    };

    if (dynamic) {
      processedValues = {
        ...processedValues,
        dynamicGroupType: processedValues.checkboxDynamicGroupType
          ? DynamicGroupTypes.FullDynamic
          : DynamicGroupTypes.Dynamic,
        removeNonActiveMembers: !processedValues.checkboxDynamicGroupType,
        criteriaNew: {
          left: "personID",
          operator: "Eq",
          right: identity.personId,
        },
      };
    }

    processedValues = {
      ...processedValues,
      validityLimit: formatDateGroup(processedValues.validityLimit),
    };

    delete processedValues.syncEgroups;

    await handleSubmit(processedValues);
  };

  const getGroupIdentifierErrorMessage = (groupIdentifier) => {
    let groupIdentifierError = null;
    if (!groupIdentifier.match(/^[a-z_\-0-9]*$/)) {
      groupIdentifierError =
        "The Group identifier contains invalid characters. Only letters, numbers, dashes and underscores are allowed.";
    } else if (
      groupIdentifier.length > 0 &&
      (!groupIdentifier.match(/^[a-z0-9]/) ||
        !groupIdentifier.match(/[a-z0-9]$/))
    ) {
      groupIdentifierError =
        "The Group identifier must start and end with a letter or number.";
    } else if (groupIdentifier.length < 3) {
      groupIdentifierError =
        "The Group identifier is too short (minimum 3 characters).";
    } else if (groupIdentifier.length > MAX_GROUP_IDENTIFIER_HARD_LIMIT) {
      groupIdentifierError =
        "The Group identifier is too long (maximum 128 characters).";
    } else if (!groupIdentifier.includes("-")) {
      groupIdentifierError =
        "The Group identifier must contain at least one dash ('-') character.";
    }
    return groupIdentifierError;
  };

  const getGroupIdentifierWarningMessage = (groupIdentifier) => {
    if (
      groupIdentifier.length > MAX_GROUP_IDENTIFIER_LENGTH &&
      groupIdentifier.length <= MAX_GROUP_IDENTIFIER_HARD_LIMIT
    ) {
      return `Group identifiers should be less than ${MAX_GROUP_IDENTIFIER_LENGTH} characters due to restrictions on downstream systems (mail archives).`;
    }
    return null;
  };

  const getDisplayNameWarningMessage = (displayName) => {
    if (
      displayName.length > MAX_GROUP_IDENTIFIER_LENGTH &&
      displayName.length <= MAX_GROUP_IDENTIFIER_HARD_LIMIT
    ) {
      return `Display name should be less than ${MAX_GROUP_IDENTIFIER_LENGTH} characters due to restrictions on downstream systems (mail archives).`;
    }
    return null;
  };

  const getDisplayNameErrorMessage = (displayName) => {
    if (displayName.length > MAX_GROUP_IDENTIFIER_HARD_LIMIT) {
      return "The display name is too long (maximum 128 characters).";
    }
    return null;
  };

  // Local quick validation on change (no uniqueness check)
  const localValidateGroupIdentifier = (value) => {
    const groupIdentifier = (value || "").toLowerCase();
    const err = getGroupIdentifierErrorMessage(groupIdentifier);
    const warning = getGroupIdentifierWarningMessage(groupIdentifier);

    if (err) {
      setGroupIdentifierStatus(FormValidationStates.Error);
      setGroupIdentifierError(err);
      setGroupIdentifierWarning("");
    } else if (warning) {
      setGroupIdentifierStatus("");
      setGroupIdentifierError("");
      setGroupIdentifierWarning(warning);
    } else {
      // clear local error and warning; final status will be set by onBlur uniqueness validator
      setGroupIdentifierStatus("");
      setGroupIdentifierError("");
      setGroupIdentifierWarning("");
    }
  };

  // Full validation executed on blur (includes uniqueness check)
  const validateGroupIdentifier = async (_, value) => {
    const groupIdentifier = (value || "").toLowerCase();

    if (!groupIdentifier) {
      const msg = "Please input the group's identifier!";
      setGroupIdentifierStatus(FormValidationStates.Error);
      setGroupIdentifierError(msg);
      return Promise.reject(new Error(msg));
    }

    setGroupIdentifierStatus(FormValidationStates.Validating);

    let groupIdentifierErr = getGroupIdentifierErrorMessage(groupIdentifier);

    if (!groupIdentifierErr) {
      try {
        const resp = await searchGroupIdentifier("GroupIdentifier", [
          groupIdentifier,
        ]);
        if (resp.payload && resp.payload.data) {
          const found = resp.payload.data.find(
            (o) => o.value === groupIdentifier
          );
          if (found && found.isTaken) {
            groupIdentifierErr = "The Group identifier is already in use.";
          }
        }
      } catch (e) {
        // network / api failure -> mark as error and show generic message
        groupIdentifierErr =
          "Could not validate uniqueness for the identifier.";
      }
    }

    if (groupIdentifierErr) {
      setGroupIdentifierStatus(FormValidationStates.Error);
      setGroupIdentifierError(groupIdentifierErr);
      setGroupIdentifierWarning("");
      return Promise.reject(new Error(groupIdentifierErr));
    }

    const warning = getGroupIdentifierWarningMessage(groupIdentifier);
    if (warning) {
      setGroupIdentifierWarning(warning);
    } else {
      setGroupIdentifierWarning("");
    }

    setGroupIdentifierStatus(FormValidationStates.Success);
    setGroupIdentifierError("");
    return Promise.resolve();
  };

  const validateGroupDisplayName = async (_, value) => {
    const groupDisplayName = value?.trim() || "";

    // Check hard limit first
    const hardLimitError = getDisplayNameErrorMessage(groupDisplayName);
    if (hardLimitError) {
      setDisplayNameStatus(FormValidationStates.Error);
      setDisplayNameError(hardLimitError);
      setDisplayNameWarning("");
      return Promise.reject(new Error(hardLimitError));
    }

    if (groupDisplayName && !/^\s/.test(value)) {
      setDisplayNameStatus(FormValidationStates.Validating);
      try {
        const resp = await searchGroupIdentifier("DisplayName", [
          groupDisplayName,
        ]);
        if (resp.payload && resp.payload.data) {
          const groupDataUpdated = resp.payload.data.find(
            (o) => o.value === groupDisplayName
          );
          if (groupDataUpdated && groupDataUpdated.isTaken) {
            setDisplayNameStatus(FormValidationStates.Error);
            setDisplayNameError("The Group display name is already taken!");
            setDisplayNameWarning("");
            return Promise.reject(
              new Error("The Group display name is already taken!")
            );
          }
        }
      } catch (e) {
        setDisplayNameStatus(FormValidationStates.Error);
        setDisplayNameError("Could not validate display name uniqueness.");
        setDisplayNameWarning("");
        return Promise.reject(
          new Error("Could not validate display name uniqueness.")
        );
      }

      const warning = getDisplayNameWarningMessage(groupDisplayName);
      if (warning) {
        setDisplayNameWarning(warning);
      } else {
        setDisplayNameWarning("");
      }

      setDisplayNameStatus(FormValidationStates.Success);
      setDisplayNameError("");
      return Promise.resolve();
    } else {
      const msg =
        "The Group display name cannot be empty or start with a space.";
      setDisplayNameStatus(FormValidationStates.Error);
      setDisplayNameError(msg);
      setDisplayNameWarning("");
      return Promise.reject(new Error(msg));
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    openNotificationsForAllValidationErrors(errorFields || []);
  };

  // compute help text combining form field errors and local error state (like original code)
  const computeFieldHelp = (fieldName, localError) => {
    const touched = form.isFieldTouched(fieldName);
    const fieldErrors = form.getFieldError(fieldName);
    const firstFieldError =
      touched && fieldErrors && fieldErrors.length ? fieldErrors[0] : null;
    return firstFieldError || localError || "";
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      onFinish={handleSubmitWrapper}
      onFinishFailed={onFinishFailed}
      initialValues={{
        validityLimit: dayjs().add(12, "M"),
        resourceCategory: resourceCategories.Test,
        selfSubscriptionType: selfSubscriptionTypes.Closed,
        syncEgroups: true,
        privacyType: privacyTypes.Admins,
      }}
    >
      <Form.Item
        label="Group identifier"
        name="groupIdentifier"
        hasFeedback
        validateStatus={groupIdentifierStatus || undefined}
        help={
          <>
            {helpText(
              computeFieldHelp("groupIdentifier", groupIdentifierError),
              "groupIdentifier"
            )}
            {warningText(groupIdentifierWarning, "groupIdentifier")}
          </>
        }
        rules={[
          { required: true, message: "Please input the group's identifier!" },
          { validator: validateGroupIdentifier },
        ]}
        validateTrigger="onBlur"
      >
        <Input
          id="groupIdentifierInput"
          aria-label="Group identifier"
          prefix={
            <UserOutlined className={styles.normalIcon} aria-hidden="true" />
          }
          maxLength={MAX_GROUP_IDENTIFIER_HARD_LIMIT}
          placeholder="my-group"
          onChange={(e) => {
            const v = (e.target.value || "").toLowerCase();
            // keep input lowercased
            form.setFieldsValue({ groupIdentifier: v });
            localValidateGroupIdentifier(v);
          }}
          onBlur={(e) => {
            const v = (e.target.value || "").toLowerCase();
            form.setFieldsValue({ groupIdentifier: v });
            // validator will run because validateTrigger is onBlur
          }}
        />
      </Form.Item>

      <Form.Item
        label="Group display name"
        name="displayName"
        hasFeedback
        validateStatus={displayNameStatus || undefined}
        help={
          <>
            {helpText(
              computeFieldHelp("displayName", displayNameError),
              "displayName"
            )}
            {warningText(displayNameWarning, "displayName")}
          </>
        }
        rules={[
          { required: true, message: "Please input the group's name!" },
          { validator: validateGroupDisplayName },
        ]}
        validateTrigger="onBlur"
      >
        <Input
          id="groupDisplayNameInput"
          aria-label="Group display name"
          placeholder="My group"
          maxLength={MAX_GROUP_IDENTIFIER_HARD_LIMIT}
        />
      </Form.Item>

      <Form.Item
        label="Description"
        name="description"
        help={helpText(computeFieldHelp("description", ""), "description")}
        rules={[
          { required: true, message: "Please input the group's description!" },
          { pattern: /\S+/, message: "Description cannot be only spaces!" },
        ]}
      >
        <Input.TextArea
          id="groupDescriptionInput"
          aria-label="Group description"
          prefix={
            <FormOutlined className={styles.normalIcon} aria-hidden="true" />
          }
          placeholder="Group description"
        />
      </Form.Item>

      <Form.Item label="Administrator group" name="administratorsId">
        <AssignAdministratorGroup />
      </Form.Item>

      {!dynamic && (
        <Form.Item
          label={
            <span>
              Purge non active users?
              <TooltipIcon title="When identities become inactive, e.g. they leave CERN, they will be removed from this group if this checkbox is ticked." />
            </span>
          }
          name="removeNonActiveMembers"
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      )}

      <Form.Item
        label={
          <span>
            Resource category
            <TooltipIcon title="Test - will be removed in a short time; Official - will be reassigned to your supervisor in case of departure; Personal - will be deleted in case of departure." />
          </span>
        }
        name="resourceCategory"
      >
        <ResourceCategoryRadioGroup />
      </Form.Item>

      <Form.Item label="Expiration date" name="validityLimit">
        <DatePicker
          disabledDate={(c) => c.isBefore(dayjs())}
          aria-label="Select group expiration date"
          placeholder="dd-mm-yyyy"
          format="DD-MM-YYYY"
        />
      </Form.Item>

      {!dynamic && (
        <Form.Item label="Self-subscription policy" name="selfSubscriptionType">
          <SelfSubscriptionRadioGroup />
        </Form.Item>
      )}

      {!dynamic && (
        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.selfSubscriptionType !== cur.selfSubscriptionType
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("selfSubscriptionType") !==
              selfSubscriptionTypes.Closed && (
              <Form.Item
                name="approvalRequired"
                label="Approval required"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            )
          }
        </Form.Item>
      )}

      <Form.Item
        label="Membership and Mail Properties visibility"
        name="privacyType"
      >
        <div>
          <PrivacyRadioGroup defaultValue={privacyTypes.Admins} />
          <br />
          <i
            style={{ color: "#ff4d4f" }}
            role="note"
            aria-label="Privacy warning"
          >
            The group memberships will be shared with other applications and
            external clouds that may not provide the same level of
            confidentiality.
          </i>
        </div>
      </Form.Item>

      <Form.Item
        label="Synchronize with E-groups?"
        name="syncEgroups"
        valuePropName="checked"
      >
        <Checkbox />
      </Form.Item>

      {dynamic && (
        <Form.Item
          label={
            <span>
              Include ex-users and ex-employees?
              <TooltipIcon title="Include all people who were registered in the CERN people database at any time to the results of the dynamic group criteria. When disabled (default option) only people with an active CERN registration will appear in the group." />
            </span>
          }
          name="checkboxDynamicGroupType"
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      )}

      <Form.Item
        label={<span>Topic </span>}
        name="topic"
        rules={[{ required: false }]}
      >
        <TopicDropdown
          title="Select optional topic"
          value={form.getFieldValue("topic") || null}
          icon={
            <FormOutlined className={styles.normalIcon} aria-hidden="true" />
          }
          onSelect={(value) => form.setFieldsValue({ topic: value })}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.displayName !== currentValues.displayName ||
          prevValues.groupIdentifier !== currentValues.groupIdentifier ||
          prevValues.description !== currentValues.description
        }
      >
        {() => (
          <Form.Item
            wrapperCol={{
              ...formItemLayout.wrapperCol,
              offset: 6,
              style: { textAlign: "right" },
            }}
          >
            <Button
              id="createGroupButton"
              color="green"
              variant="solid"
              loading={loading}
              htmlType="submit"
              aria-label="Create new group"
              disabled={
                !(
                  (displayNameStatus === FormValidationStates.Success ||
                    displayNameStatus === "") &&
                  !displayNameError &&
                  (groupIdentifierStatus === FormValidationStates.Success ||
                    groupIdentifierStatus === "") &&
                  !groupIdentifierError &&
                  !!form.getFieldValue("description")
                )
              }
            >
              Create
            </Button>
          </Form.Item>
        )}
      </Form.Item>
    </Form>
  );
};

GroupCreationForm.propTypes = {
  groupScopes: PropTypes.array.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dynamic: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  searchGroupIdentifier: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    groupScopes: state.groupScope,
  }),
  { searchGroupIdentifier }
)(GroupCreationForm);
